import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Logo3D from "./Logo3D";

/**
 * RouteLoader — a brief full-screen overlay shown on EVERY page/menu switch.
 * Watches the route (location.pathname); when it changes, it shows a spinning
 * 3D company logo + "Loading..." for a short beat, then fades out.
 *
 * Purely visual feedback for navigation. It does not block anything — the
 * destination page renders behind it and is revealed on fade. The logo shown
 * rotates through the three sister companies per navigation so it isn't always
 * the same mark.
 *
 * Tunables: SHOW_MS (how long it stays), FADE_MS (fade duration).
 */

const SHOW_MS = 1100;
const FADE_MS = 350;

const RouteLoader = () => {
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [fading, setFading] = useState(false);
  const firstRef = useRef(true);
  const timersRef = useRef([]);

  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return undefined;
    }

    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];

    setActive(true);
    setFading(false);

    const t1 = setTimeout(() => setFading(true), SHOW_MS);
    const t2 = setTimeout(() => {
      setActive(false);
      setFading(false);
    }, SHOW_MS + FADE_MS);
    timersRef.current = [t1, t2];

    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current = [];
    };
  }, [location.pathname]);

  if (!active) return null;

  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 12000,
        background: "#ffffff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        opacity: fading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        pointerEvents: "none",
      }}
    >
      <Logo3D company="combined" size={220} secPerTurn={4} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#16447a",
          fontFamily: "'Inter', -apple-system, 'Segoe UI', Roboto, sans-serif",
        }}
      >
        Loading...
      </span>
    </div>
  );
};

export default RouteLoader;
